<template>
  <v-container class="product-license" grid-list-md>
    <!-- Connect/Integrations -->
    <v-layout v-if="type == 'integration'" wrap class="">
      <v-flex xs12>
        <v-divider></v-divider>
      </v-flex>

      <v-flex v-if="integrationTransactions.length" xs12 sm7 md8 text-left>
        <v-card v-if="integrationTransactions.length" flat>
          <div class="text-h5 font-weight-bold text-uppercase">
            {{ $t('licenses.e6connectintegration') }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="integrationTransactions.length && !isProductDetails"
        xs12
        sm5
        md4
        text-left
        text-sm-right
        class="pb-0"
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'connect',
              },
            }"
          >
            {{ $t('licenses.addIntegration') }}
          </v-btn>
        </v-card>
      </v-flex>

      <v-flex v-if="integrationTransactions.length" xs12 class="pt-0">
        <v-card flat>
          <div
            v-for="{
              recordId,
              webTitle,
              skuCode,
              productID,
              productCategory,
              purchased,
              redeemedAt,
              createdAt,
              transactionID,
              licenseID,
              adminRedeemed,
              deactivatedAt,
              expirationDate,
              count,
              isActive = false,
            } in integrationTransactions"
            :key="recordId"
            class="py-1x"
          >
            <transition name="fadeDown" appear>
              <v-layout v-show="showInactive || isActive" wrap>
                <v-flex xs12 sm7 md8 text-left>
                  <router-link
                    :to="{
                      props: true,
                      name: 'productDetails',
                      params: {
                        productId: productID.toString(),
                        category: 'connect' || productCategory,
                      },
                    }"
                  >
                    <div
                      :class="[
                        'title font-weight-bold',
                        `${$vuetify.theme.dark ? 'primary--text text--lighten-1' : ''}`,
                      ]"
                    >
                      {{ webTitle || skuCode }}
                    </div>
                  </router-link>
                  <div
                    v-if="purchased || redeemedAt || createdAt"
                    class="license-title font-weight-regular"
                  >
                    <div v-if="deactivatedAt" class="text-caption font-weight-bold">
                      Deactivated:
                      {{ deactivatedAt | utility_formatDateAndTime }}
                    </div>
                    <div v-if="!adminRedeemed" class="text-caption license-expiration">
                      {{ checkForProductCode(transactionID) ? `Redeemed: ` : `Purchased: ` }}
                      {{
                        checkForProductCode(transactionID)
                          ? redeemedAt
                          : createdAt | utility_formatDateAndTime
                      }}
                    </div>
                    <div
                      v-if="
                        expirationDate &&
                        !deactivatedAt &&
                        !checkFarFutureExpiration(expirationDate)
                      "
                      class="text-caption license-expiration"
                    >
                      Expire{{ checkExpiration(expirationDate) ? 'd' : 's' }}:
                      {{ expirationDate | utility_formatDateAndTime }}
                    </div>
                    <div v-if="adminRedeemed" class="text-caption">
                      Admin Added:
                      {{ redeemedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="
                        redeemedAt &&
                        transactionID &&
                        !purchased &&
                        checkForProductCode(transactionID)
                      "
                      class="text-caption license-expiration"
                    >
                      Product Key:
                      {{ transactionID }}
                    </div>
                    <div v-if="count > 1" class="text-caption license-expiration">
                      Quantity: {{ count }}
                    </div>
                  </div>
                </v-flex>
                <v-flex
                  v-if="canViewAdmin && viewingAdmin && !isProductDetails"
                  xs12
                  sm5
                  md4
                  text-left
                  text-sm-right
                >
                  <v-btn
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="!!deactivatedAt"
                    @click="deactivateProduct(transactionID, webTitle, licenseID)"
                  >
                    Deactivate Product
                  </v-btn>
                </v-flex>
              </v-layout>
            </transition>
          </div>
        </v-card>
      </v-flex>

      <v-flex v-else xs12 sm8 text-left>
        <v-card flat>
          <div class="text-h5 font-weight-bold">
            {{ $t('licenses.noIntegrations').toUpperCase() }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="!integrationTransactions.length && !isProductDetails"
        xs12
        sm4
        text-left
        text-sm-right
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'connect',
              },
            }"
          >
            {{ $t('licenses.addIntegration') }}
          </v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- subscriptions -->
    <!-- ====================================================================================== -->
    <v-layout v-if="type == 'subscription'" wrap class="">
      <v-flex xs12>
        <v-divider></v-divider>
      </v-flex>

      <v-flex v-if="subscriptionTransactions.length" xs12 sm7 md8 text-left class="pb-0">
        <v-card v-if="subscriptionTransactions.length" flat>
          <div class="text-h5 font-weight-bold text-uppercase">
            {{ $t('licenses.subscriptions') }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="subscriptionTransactions.length && !isProductDetails"
        xs12
        sm5
        md4
        text-left
        text-sm-right
        class="pb-0"
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'subscriptions',
              },
            }"
          >
            {{ $t('licenses.addSubscription') }}
          </v-btn>
        </v-card>
      </v-flex>

      <v-flex v-if="subscriptionTransactions.length" xs12 class="pt-0">
        <v-card flat>
          <!-- loop over subcription transactions here -->
          <div
            v-for="{
              recordID,
              webTitle,
              skuCode, // use for upgrade
              subscriptionStatus, // use to check if able to upgrade
              originalTransactionID, // use for upgrade - check for sub_
              cancelledAt, // use to check if able to upgrade
              productID,
              licenseID,
              productType,
              productCategory,
              latestTransaction,
              initialPurchase,
              expirationDate,
              redeemedAt,
              transactionID, // use for upgrade - check for sub_
              adminRedeemed,
              conversionProduct,
              deactivatedAt,
              count,
              isActive = false,
              licenseType,
              subShowUpgrade,
            } in subscriptionTransactions"
            :key="`${recordID}${originalTransactionID}${subscriptionStatus}`"
            class="py-1x"
          >
            <transition name="fadeDown" appear>
              <v-layout v-if="showInactive || isActive" wrap>
                <v-flex xs12 sm7 md8 text-left>
                  <router-link
                    :to="{
                      props: true,
                      name: 'productDetails',
                      params: {
                        productId: productID.toString(),
                        category: 'subscriptions' || productCategory,
                      },
                    }"
                  >
                    <div
                      :class="[
                        'title font-weight-bold',
                        `${$vuetify.theme.dark ? 'primary--text text--lighten-1' : ''}`,
                      ]"
                    >
                      {{ webTitle || skuCode }}
                      <v-tooltip v-if="utility_hasClipboard && isAdmin && canViewAdmin" top>
                        <template #activator="{ on }">
                          <v-icon
                            small
                            @click.prevent="
                              utility_copyText(getSubID(originalTransactionID, transactionID))
                            "
                            v-on="on"
                          >
                            content_copy
                          </v-icon>
                        </template>
                        <span>Copy Transaction ID</span>
                      </v-tooltip>
                    </div>
                  </router-link>
                  <div class="license-title font-weight-regular">
                    <div v-if="deactivatedAt" class="text-caption font-weight-bold">
                      Deactivated:
                      {{ deactivatedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="redeemedAt && !adminRedeemed"
                      class="text-caption license-expiration"
                    >
                      {{
                        checkForProductCode(originalTransactionID)
                          ? `Redeemed: `
                          : `Purchase${latestTransaction && !initialPurchase ? ' Renewed' : 'd'}: `
                      }}
                      <!-- Purchase{{ latestTransaction && !initialPurchase ? ' Renewed' : 'd' }}: -->
                      {{ redeemedAt | utility_formatDateAndTime }}
                    </div>
                    <div v-if="adminRedeemed" class="text-caption">
                      Admin Added:
                      {{ redeemedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="
                        redeemedAt &&
                        originalTransactionID &&
                        checkForProductCode(originalTransactionID)
                      "
                      class="text-caption license-expiration"
                    >
                      Product Key:
                      {{ originalTransactionID }}
                    </div>
                    <div v-if="subscriptionStatus" class="text-caption license-expiration">
                      Status: {{ utility_capitalize(subStatusFormat(subscriptionStatus)) }}
                      <span v-if="isCancelled(subscriptionStatus)">
                        {{ cancelledAt | utility_formatDateAndTime }}
                      </span>
                    </div>
                    <div
                      v-if="
                        expirationDate &&
                        !deactivatedAt &&
                        !checkFarFutureExpiration(expirationDate)
                      "
                      class="text-caption license-expiration"
                    >
                      {{
                        subscriptionStatus &&
                        (subscriptionStatus.toLowerCase() === 'active' ||
                          subscriptionStatus.toLowerCase() === 'trialing')
                          ? `${$t('licenses.renews')}`
                          : `Expire${checkExpiration(expirationDate) ? 'd' : 's'}`
                      }}:
                      {{ expirationDate | utility_formatDateAndTime }}
                    </div>
                    <div v-if="count > 1" class="text-caption license-expiration">
                      Quantity: {{ count }}
                    </div>
                    <div v-show="subShowUpgrade" class="text-caption mt-2">
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <v-btn
                              color="primary"
                              :disabled="
                                isAdmin ||
                                (subscriptionStatus &&
                                  subscriptionStatus.toLowerCase() === 'trialing' &&
                                  !checkForProductCode(originalTransactionID))
                              "
                              small
                              @click.prevent="
                                upgradeInit(
                                  webTitle,
                                  skuCode,
                                  subscriptionStatus,
                                  originalTransactionID,
                                  transactionID,
                                  licenseID,
                                  licenseType,
                                  productCategory
                                )
                              "
                            >
                              {{ $t('general.upgrade') }}
                            </v-btn>
                          </span>
                        </template>
                        <span>{{
                          subscriptionStatus &&
                          subscriptionStatus.toLowerCase() === 'trialing' &&
                          !checkForProductCode(originalTransactionID)
                            ? $t('licenses.unableToUpgrade')
                            : $t('licenses.upgradeTooltip')
                        }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-flex>
                <v-flex
                  v-if="!viewingAdmin && !(conversionProduct || adminRedeemed) && !isProductDetails"
                  xs12
                  sm5
                  md4
                  text-left
                  text-sm-right
                >
                  <v-btn
                    v-if="
                      productType === 'subscription' ||
                      productType === 'consumable' ||
                      (productType === 'purchase' &&
                        productCategory === 'licenseSubscription' &&
                        hasStripeSub(originalTransactionID, transactionID))
                    "
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="
                      isCancelled(subscriptionStatus) ||
                      !!deactivatedAt ||
                      disableCancelCheck(originalTransactionID, transactionID)
                    "
                    @click="
                      cancelSubscriptionCheck(
                        originalTransactionID,
                        webTitle,
                        licenseID,
                        transactionID,
                        productCategory,
                        licenseType
                      )
                    "
                  >
                    {{ $t('licenses.cancelRenewal') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  v-else-if="canViewAdmin && viewingAdmin && !isProductDetails"
                  xs12
                  sm5
                  md4
                  text-left
                  text-sm-right
                >
                  <v-btn
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="!!deactivatedAt"
                    @click="
                      deactivateProduct(transactionID, webTitle, licenseID, originalTransactionID)
                    "
                  >
                    Deactivate Product
                  </v-btn>
                  <v-btn
                    v-if="
                      productType === 'subscription' ||
                      productType === 'consumable' ||
                      (productType === 'purchase' &&
                        productCategory === 'licenseSubscription' &&
                        hasStripeSub(originalTransactionID, transactionID))
                    "
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="
                      isCancelled(subscriptionStatus) ||
                      !!deactivatedAt ||
                      disableCancelCheck(originalTransactionID, transactionID)
                    "
                    @click="
                      cancelSubscriptionCheck(
                        originalTransactionID,
                        webTitle,
                        licenseID,
                        transactionID,
                        productCategory,
                        licenseType
                      )
                    "
                  >
                    {{ $t('licenses.cancelRenewal') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </transition>
          </div>
        </v-card>
      </v-flex>

      <v-flex v-else xs12 sm8 text-left>
        <v-card flat>
          <div class="text-h5 font-weight-bold">
            {{ $t('licenses.noSubscriptions').toUpperCase() }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="!subscriptionTransactions.length && !isProductDetails"
        xs12
        sm4
        text-left
        text-sm-right
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'subscriptions',
              },
            }"
          >
            {{ $t('licenses.addSubscription') }}
          </v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- courses -->
    <v-layout v-if="type == 'purchase'" wrap class="">
      <v-flex xs12>
        <v-divider></v-divider>
      </v-flex>

      <v-flex v-if="purchaseTransactions.length" xs12 sm7 md8 text-left>
        <v-card flat>
          <div class="text-h5 font-weight-bold text-uppercase">
            {{ $t('licenses.premiumcourses') }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="purchaseTransactions.length && !isProductDetails"
        xs12
        sm5
        md4
        text-left
        text-sm-right
        class="pb-0"
      >
        <v-btn
          text
          small
          :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
          class="mr-0"
          :to="{
            props: true,
            name: 'Products',
            params: {
              category: 'premiumcourses',
            },
          }"
        >
          {{ $t('licenses.addCourse') }}
        </v-btn>
      </v-flex>

      <v-flex v-if="purchaseTransactions.length" xs12 class="pt-0">
        <v-card flat>
          <div
            v-for="{
              recordId,
              webTitle,
              skuCode,
              productID,
              productCategory,
              purchased,
              createdAt,
              redeemedAt,
              transactionID,
              licenseID,
              adminRedeemed,
              deactivatedAt,
              expirationDate,
              count,
              isActive = false,
            } in purchaseTransactions"
            :key="recordId"
            class="py-1x"
          >
            <transition name="fadeDown" appear>
              <v-layout v-show="showInactive || isActive" wrap>
                <v-flex xs12 sm7 md8 text-left>
                  <router-link
                    :to="{
                      props: true,
                      name: 'productDetails',
                      params: {
                        productId: productID.toString(),
                        category: 'premiumcourses' || productCategory,
                      },
                    }"
                  >
                    <div
                      :class="[
                        'title font-weight-bold',
                        `${$vuetify.theme.dark ? 'primary--text text--lighten-1' : ''}`,
                      ]"
                    >
                      {{ webTitle || skuCode }}
                    </div>
                  </router-link>
                  <div v-if="createdAt || redeemedAt" class="license-title font-weight-regular">
                    <div v-if="deactivatedAt" class="text-caption font-weight-bold">
                      Deactivated:
                      {{ deactivatedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="
                        expirationDate &&
                        !deactivatedAt &&
                        !checkFarFutureExpiration(expirationDate)
                      "
                      class="text-caption license-expiration"
                    >
                      Expire{{ checkExpiration(expirationDate) ? 'd' : 's' }}:
                      {{ expirationDate | utility_formatDateAndTime }}
                    </div>
                    <div v-if="!adminRedeemed" class="text-caption license-expiration">
                      {{ checkForProductCode(transactionID) ? `Redeemed: ` : `Purchased: ` }}
                      {{
                        checkForProductCode(transactionID)
                          ? redeemedAt
                          : createdAt | utility_formatDateAndTime
                      }}
                    </div>
                    <div v-if="adminRedeemed" class="text-caption">
                      Admin Added:
                      {{ redeemedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="
                        redeemedAt &&
                        transactionID &&
                        !purchased &&
                        checkForProductCode(transactionID)
                      "
                      class="text-caption license-expiration"
                    >
                      Product Key:
                      {{ transactionID }}
                    </div>
                    <div v-if="count > 1" class="text-caption license-expiration">
                      Quantity: {{ count }}
                    </div>
                  </div>
                </v-flex>
                <v-flex
                  v-if="canViewAdmin && viewingAdmin && !isProductDetails"
                  xs12
                  sm5
                  md4
                  text-left
                  text-sm-right
                >
                  <v-btn
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="!!deactivatedAt"
                    @click="deactivateProduct(transactionID, webTitle, licenseID)"
                  >
                    Deactivate Product
                  </v-btn>
                </v-flex>
              </v-layout>
            </transition>
          </div>
        </v-card>
      </v-flex>

      <v-flex v-else xs12 sm8 text-left>
        <v-card flat>
          <div class="text-h5 font-weight-bold">
            {{ $t('licenses.noCourses').toUpperCase() }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="!purchaseTransactions.length && !isProductDetails"
        xs12
        sm4
        text-left
        text-sm-right
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'premiumcourses',
              },
            }"
          >
            {{ $t('licenses.addCourse') }}
          </v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- add-ons -->
    <v-layout v-if="type == 'consumable'" wrap class="">
      <v-flex xs12>
        <v-divider></v-divider>
      </v-flex>

      <v-flex v-if="consumableTransactions.length" xs12 sm7 md8 text-left>
        <v-card v-if="consumableTransactions.length" flat>
          <div class="text-h5 font-weight-bold text-uppercase">
            {{ $t('licenses.addons') }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="consumableTransactions.length && !isProductDetails"
        xs12
        sm5
        md4
        text-left
        text-sm-right
        class="pb-0"
      >
        <v-btn
          text
          small
          :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
          class="mr-0"
          :to="{
            props: true,
            name: 'Products',
            params: {
              category: 'premiumcourses',
            },
          }"
        >
          {{ $t('licenses.addAddons') }}
        </v-btn>
      </v-flex>

      <v-flex v-if="consumableTransactions.length" xs12 class="pt-0">
        <v-card flat>
          <div
            v-for="{
              recordId,
              webTitle,
              skuCode,
              redeemedAt,
              createdAt,
              transactionID,
              productID,
              productCategory,
              purchased,
              adminRedeemed,
              licenseID,
              deactivatedAt,
              expirationDate,
              count,
              isActive = false,
            } in consumableTransactions"
            :key="recordId"
            class="py-1x"
          >
            <transition name="fadeDown" appear>
              <v-layout v-show="showInactive || isActive" wrap>
                <v-flex xs12 sm7 md8 text-left>
                  <router-link
                    :to="{
                      props: true,
                      name: 'productDetails',
                      params: {
                        productId: productID.toString(),
                        category: 'multisport' || productCategory,
                      },
                    }"
                  >
                    <div
                      :class="[
                        'title font-weight-bold',
                        `${$vuetify.theme.dark ? 'primary--text text--lighten-1' : ''}`,
                      ]"
                    >
                      {{ webTitle || skuCode }}
                    </div>
                  </router-link>
                  <div
                    v-if="purchased || redeemedAt || createdAt"
                    class="license-title font-weight-regular"
                  >
                    <div v-if="deactivatedAt" class="text-caption font-weight-bold">
                      Deactivated:
                      {{ deactivatedAt | utility_formatDateAndTime }}
                    </div>
                    <div v-if="!adminRedeemed" class="text-caption license-expiration">
                      {{ checkForProductCode(transactionID) ? `Redeemed: ` : `Purchased: ` }}
                      {{
                        checkForProductCode(transactionID)
                          ? redeemedAt
                          : createdAt | utility_formatDateAndTime
                      }}
                    </div>
                    <div
                      v-if="
                        expirationDate &&
                        !deactivatedAt &&
                        !checkFarFutureExpiration(expirationDate)
                      "
                      class="text-caption license-expiration"
                    >
                      Expire{{ checkExpiration(expirationDate) ? 'd' : 's' }}:
                      {{ expirationDate | utility_formatDateAndTime }}
                    </div>
                    <div v-if="adminRedeemed" class="text-caption">
                      Admin Added:
                      {{ redeemedAt | utility_formatDateAndTime }}
                    </div>
                    <div
                      v-if="
                        redeemedAt &&
                        transactionID &&
                        !purchased &&
                        checkForProductCode(transactionID)
                      "
                      class="text-caption license-expiration"
                    >
                      Product Key:
                      {{ transactionID }}
                    </div>
                    <div v-if="count > 1" class="text-caption license-expiration">
                      Quantity: {{ count }}
                    </div>
                  </div>
                </v-flex>
                <v-flex
                  v-if="canViewAdmin && viewingAdmin && !isProductDetails"
                  xs12
                  sm5
                  md4
                  text-left
                  text-sm-right
                >
                  <v-btn
                    text
                    small
                    :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
                    class="mr-0"
                    :disabled="!!deactivatedAt"
                    @click="deactivateProduct(transactionID, webTitle, licenseID)"
                  >
                    Deactivate Product
                  </v-btn>
                </v-flex>
              </v-layout>
            </transition>
          </div>
        </v-card>
      </v-flex>

      <v-flex v-else xs12 sm8 text-left>
        <v-card flat>
          <div class="text-h5 font-weight-bold">
            {{ $t('licenses.noAddOns').toUpperCase() }}
          </div>
        </v-card>
      </v-flex>

      <v-flex
        v-if="!consumableTransactions.length && !isProductDetails"
        xs12
        sm4
        text-left
        text-sm-right
      >
        <v-card flat>
          <v-btn
            text
            small
            :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'info'}`"
            class="mr-0"
            :to="{
              props: true,
              name: 'Products',
              params: {
                category: 'premiumcourses',
              },
            }"
          >
            {{ $t('licenses.addAddons') }}
          </v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="mobileSubDialog" transition="dialog-bottom-transition" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" style="word-break: break-word">
          {{ $t('info.attention').toUpperCase() }}!
        </v-card-title>

        <v-card-text class="text-subtitle-1 pt-3">
          {{ $t('info.mobileSubCancelMsg') }}
        </v-card-text>
        <v-card-text class="text-center">
          <v-btn color="info">
            <a href="https://support.apple.com/en-us/HT202039" class="white--text" target="_blank">
              {{ $t('info.mobileSubCancelBtn') }}
            </a>
          </v-btn>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click.native="mobileSubDialog = false"> {{ $t('general.close') }} </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelSubDialog" transition="dialog-bottom-transition" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" style="word-break: break-word">
          {{ $t('info.attention').toUpperCase() }}!
        </v-card-title>

        <v-card-text v-if="subscriptionInfoToCancel" class="text-subtitle-1 pt-3">
          {{ $t('licenses.cancelRenewalMsg') }}:
          <div class="font-weight-bold">{{ subscriptionInfoToCancel.title }}?</div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="subCancelLoading" @click.native="closeCancelSubscription">
            {{ $t('general.cancel') }}
          </v-btn>
          <v-btn
            text
            :loading="subCancelLoading"
            color="primary"
            @click.native="cancelSubscription(subscriptionInfoToCancel)"
          >
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="tgSubCancelDialog" transition="dialog-bottom-transition" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" style="word-break: break-word">
          {{ $t('info.attention').toUpperCase() }}!
        </v-card-title>

        <v-card-text class="text-subtitle-1 pt-3">
          Please contact TruGolf to cancel this subscription at Toll Free: 866-711-GOLF
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click.native="closeCancelSubscription"> {{ $t('general.close') }} </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="upgradeInitDialog"
      transition="dialog-bottom-transition"
      width="750"
      overlay-opacity=".6"
    >
      <v-card :dark="$vuetify.theme.dark ? true : false" style="overflow: hidden">
        <v-card-title class="text-h5 primary white--text" style="word-break: break-word">
          {{ $t('licenses.upgradeHeader') }}
        </v-card-title>

        <v-card-text v-if="loadingUpgradeInit" class="text-subtitle-1 pt-3">
          <div class="progress mt-4">
            <pulse-loader :color="$options.loaderColor" :size="$options.loaderSize"></pulse-loader>
          </div>
        </v-card-text>

        <v-card-text v-else class="text-subtitle-1 pt-3">
          <h3 v-if="!upgradeDetails && upgradeStep === 1">
            {{ $t('licenses.upgradeOptionHeader') }}:
          </h3>
          <h3 v-else-if="upgradeDetails && upgradeStep === 2">
            {{ $t('licenses.upgradeConfirmDetails') }}:
          </h3>

          <v-row
            v-if="
              upgradeProducts &&
              !upgradeProducts.length &&
              !loadingUpgradeInit &&
              !upgradeDetails &&
              upgradeStep === 1
            "
            wrap
            class="mt-2"
          >
            <v-col cols="12" sm="12">
              <v-alert outlined type="info">
                No products available to upgrade to at this time. Please check back later.
              </v-alert>
            </v-col>
          </v-row>

          <v-row
            v-if="
              upgradeProducts &&
              upgradeProducts.length &&
              !loadingUpgradeInit &&
              !upgradeDetails &&
              upgradeStep === 1
            "
            wrap
            class="mt-2"
          >
            <v-col
              v-for="product in upgradeProducts"
              :key="product.productID"
              cols="12"
              sm="6"
              md="4"
            >
              <transition-group name="fade" appear>
                <Product
                  :key="product.productID"
                  :product-id="product.productID.toString()"
                  :product="product"
                  :loading="loadingUpgradeInit"
                  simple-product
                  @addToCartUpgrade="addToCartUpgrade"
                />
              </transition-group>
            </v-col>
            <v-col cols="12" sm="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12" sm="12">
              <transition name="fadeDown" appear>
                <v-img
                  max-width="100%"
                  max-height="390"
                  alt="Subscription Comparison Chart"
                  src="https://trugolf.com/cdn/shop/files/E6_subscription_comparison-chart_3_c5fb624d-d876-4b1d-8d28-9e15716c5115.webp?v=1733429455"
                  :lazy-src="$options.lazyImage"
                >
                  <template #placeholder>
                    <v-layout fill-height align-center justify-center ma-0>
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      ></v-progress-circular>
                    </v-layout>
                  </template>
                </v-img>
              </transition>
            </v-col>
          </v-row>
          <transition v-else-if="upgradeDetails && upgradeStep === 2" name="fadeDown" appear>
            <v-row wrap class="mt-4">
              <v-col cols="12" sm="6" class="font-weight-bold">
                {{ $t('licenses.upgradeDetailsProductHeader') }}:
              </v-col>
              <v-col cols="12" sm="6">
                {{ upgradeDetails.webTitle }}
              </v-col>
              <v-col cols="12" sm="6" class="font-weight-bold">
                {{ $t('licenses.upgradeDetailsExpirationHeader') }}:
              </v-col>
              <v-col cols="12" sm="6">
                {{ upgradeDetails.new_expiration | utility_formatDate }}
              </v-col>
              <v-col cols="12" sm="6" class="font-weight-bold">
                {{ $t('licenses.upgradeDetailsNewPriceHeader') }}:
              </v-col>
              <v-col cols="12" sm="6">
                {{ $n(utility_centsToDollars(upgradeDetails.new_price), 'currency') }}
              </v-col>
              <v-col cols="12" sm="6" class="font-weight-bold">
                {{ $t('licenses.upgradeDetailsCreditHeader') }}:
              </v-col>
              <v-col cols="12" sm="6">
                {{ $n(utility_centsToDollars(upgradeDetails.credit), 'currency') }}
              </v-col>
            </v-row>
          </transition>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="upgradeDetails" text @click.native="upgradeStepBack">
            &larr; {{ $t('general.back') }}
          </v-btn>
          <v-btn text @click.native="closeUpgrade"> {{ $t('general.close') }} </v-btn>
          <v-btn v-if="upgradeDetails" text @click.native="confirmUpgradeDetails">
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import utilityMixin from '@/mixins/utilityMixin';
import { loaderColor, loaderSize, lazyProductImageTG } from '@/lib/constants';

const Product = () => {
  return import(/* webpackPrefetch: true */ '@/components/Product');
};

export default {
  name: 'LicenseItemProduct',

  components: {
    Product,
  },

  mixins: [utilityMixin],

  props: {
    integrationTransactions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    subscriptionTransactions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    purchaseTransactions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    consumableTransactions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    transactions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    type: {
      type: String,
      default: '',
    },
    license: {
      type: Object,
      default: () => {
        return {};
      },
    },
    canViewAdmin: {
      type: Boolean,
      default: false,
    },
    showInactive: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      componentKey: 0,
      mobileSubDialog: false,
      cancelSubDialog: false,
      tgSubCancelDialog: false,
      subCancelLoading: false,
      subscriptionInfoToCancel: null,
      loadingUpgradeInit: false,
      upgradeInitDialog: false,
      showUpgrade: false,
      upgradeProducts: [],
      selectedLicenseToUpgrade: null,
      upgradeDetails: null,
      subscriptionToUpgrade: null,
      newSubscriptionProduct: null,
      licensedUser: null,
      upgradeStep: 1,
      skuHierarchy: ['IRS', 'IRS1', 'BS1', 'PLA', 'ENJ', 'EXP', 'EX1'],

      //! Set this to true and deploy to disable the subscription upgrade option
      disableUpgradeFlow: false,
    };
  },

  computed: {
    subscriptionButtonCheck() {
      if (this.transactions) {
        return this.transactions.filter((item) => {
          return item?.subscriptionStatus?.toLowerCase()?.includes('cancel') || false;
        });
      }
      return false;
    },

    viewingAdmin() {
      return this.$route.fullPath.includes('admin');
    },

    isProductDetails() {
      return this.$route.name === 'productDetails';
    },
  },

  watch: {
    subscriptionTransactions: {
      handler(newVal) {
        // Run upgrade check when subscriptionTransactions changes
        if (newVal?.length && !this.disableUpgradeFlow) {
          this.runUpgradeCheck();
        }
      },
      immediate: true, // Also run on initial mount
    },
  },

  created() {
    //* non-reactive constants
    this.$options.loaderColor = loaderColor;
    this.$options.loaderSize = loaderSize;
    this.$options.lazyImage = lazyProductImageTG;
  },

  mounted() {
    //   if (this.license?.license?.id === 'ecb944f9-42b1-4830-9d6b-ff55adf0f040') {
    //     console.log(this.license);
    //     console.log(this.subscriptionTransactions);
    //     console.log('===============================================');
    //   }
    //   // console.log(this.$route);
    // if (this.subscriptionTransactions?.length && !this.disableUpgradeFlow) {
    //   this.subscriptionTransactions?.forEach((item) => {
    //     const val = this.checkCanUpgrade(item);
    //     item.subShowUpgrade = val;
    //     this.licensedUser = item.userID;
    //   });
    // }
  },

  methods: {
    closeUpgrade() {
      this.upgradeInitDialog = false;
      this.upgradeProducts = [];
      this.selectedLicenseToUpgrade = null;
      this.upgradeDetails = null;
      this.upgradeStep = 1;
      this.subscriptionToUpgrade = null;
      this.newSubscriptionProduct = null;
      this.licensedUser = null;
    },

    runUpgradeCheck() {
      if (this.subscriptionTransactions?.length && !this.disableUpgradeFlow) {
        this.subscriptionTransactions.forEach((item) => {
          const val = this.checkCanUpgrade(item);
          item.subShowUpgrade = val;
          this.licensedUser = item.userID;
        });
      }
    },

    // Initial check to know if subscription can be upgraded and display button - i.e. basic to expanded
    checkCanUpgrade({
      webTitle: title,
      skuCode: sku,
      subscriptionStatus: subStatus,
      originalTransactionID: origTransactionID,
      transactionID,
      cancelledAt,
    }) {
      // If disableUpgradeFlow set to true, it will disable the upgrade button option
      if (this.disableUpgradeFlow) {
        this.showUpgrade = false;
        return false;
      }

      // console.log('title:', title);
      // console.log('sku:', sku);
      // console.log('subStatus:', subStatus);
      // console.log('origTransactionID:', origTransactionID);
      // console.log('transactionID:', transactionID);
      // console.log('cancelledAt:', cancelledAt);

      // Check to make sure we get the stripe sub_ subscription ID
      const subscriptionID = origTransactionID?.startsWith('sub')
        ? origTransactionID
        : transactionID;

      // If subscription is a stripe sub, display upgrade button
      if (
        subscriptionID?.startsWith('sub_') &&
        !title?.toLowerCase()?.includes('expanded') &&
        subStatus !== 'cancelled' &&
        !cancelledAt
      ) {
        // console.log('S======================================================');
        // console.log('Show upgrade', title, sku, subscriptionID);
        // console.log('E======================================================');
        this.showUpgrade = true;
        return true;
      }

      this.showUpgrade = false;
      return false;
    },

    upgradeInit(
      title,
      sku,
      subStatus,
      origTransactionID,
      transactionID,
      licenseID,
      licenseType,
      productCategory
    ) {
      this.loadingUpgradeInit = true;
      this.upgradeInitDialog = true;
      // console.log('upgradeInit__________________________________');

      if (subStatus === 'trialing' && !this.checkForProductCode(origTransactionID)) {
        this.loadingUpgradeInit = false;
        this.upgradeInitDialog = false;
        alert(this.$t('licenses.unableToUpgrade'));
        return;
      }

      // Move this to separate function
      // Check to make sure we get the stripe sub_ subscription ID
      const subscriptionID = origTransactionID?.startsWith('sub')
        ? origTransactionID
        : transactionID;

      // If mobile subscription through iOS, display iOS cancellation dialog
      if (
        productCategory === 'licenseSubscription' &&
        !subscriptionID.startsWith('sub_') &&
        !origTransactionID?.startsWith('trugolf_admin') &&
        !transactionID?.startsWith('trugolf_admin')
      ) {
        this.mobileSubDialog = true;
        return;
      }
      // TG Admin added subscription. Can't cancel by normal means. Display contact TG dialog.
      if (
        origTransactionID.startsWith('trugolf_admin') ||
        (!origTransactionID.startsWith('sub_') && !transactionID.startsWith('sub_'))
      ) {
        this.tgSubCancelDialog = true;
        return;
      }

      this.subscriptionToUpgrade = {
        title,
        sku,
        subStatus,
        origTransactionID,
        transactionID,
        licenseID,
        licenseType,
      };
      this.selectedLicenseToUpgrade = licenseID;

      // get upgrade product list
      this.$store
        .dispatch('shop/subscriptionUpgradeProductList', {
          sku,
        })
        .then((res) => {
          // console.log('subscriptionUpgradeProductList res:', res);

          // Filter to only show applicable products
          this.upgradeProducts = this.upgradeProductFilteredList(res, this.subscriptionToUpgrade);
          this.loadingUpgradeInit = false;
        })
        .catch((err) => {
          console.error('error:', err);
          this.loadingUpgradeInit = false;
          this.upgradeProducts = [];
          const { data } = err || {};
          this.$root.vtoast.show({
            text: data?.error?.msg || 'Error fetching upgrade options',
            color: 'error',
          });
        });
    },

    addToCartUpgrade(data) {
      this.loadingUpgradeInit = true;
      this.newSubscriptionProduct = data;
      this.upgradeStep = 2;
      // console.log('data:', data);
      // console.log('LICENSE: ', this.selectedLicenseToUpgrade);
      const { title, sku, subStatus, origTransactionID, transactionID, licenseID, licenseType } =
        this.subscriptionToUpgrade;

      const subscriptionID = origTransactionID?.startsWith('sub')
        ? origTransactionID
        : transactionID;

      setTimeout(() => {
        this.loadingUpgradeInit = false;
      }, 1500);

      this.$store
        .dispatch('shop/subscriptionUpgradeCheck', {
          skucode: data.skuCode,
          subscription_id: subscriptionID,
        })
        .then((res) => {
          // console.log('res:', res);
          this.upgradeDetails = {
            ...res,
            webTitle: data?.webTitle || 'N/A',
            licenseType,
          };
        })
        .catch((err) => {
          console.error('error:', err);
          const { data } = err || {};
          this.loadingUpgradeInit = false;
          this.$root.vtoast.show({
            text: data?.error?.msg || 'Error checking upgrade',
            color: 'error',
          });
        });
    },

    confirmUpgradeDetails() {
      // console.log('confirm and add to cart here----');
      // console.log('sub to upgrade', this.subscriptionToUpgrade);
      // console.log('new product:', this.newSubscriptionProduct);
      const license = {
        ...this.subscriptionToUpgrade,
      };
      this.addToCart(this.newSubscriptionProduct, license, this.upgradeDetails?.subscription_id);
    },

    addToCart(product = {}, license = '', subscription = '') {
      // console.log('product:', product);
      // console.log('license:', license);
      // console.log('subscription:', subscription);

      product.user = {
        id: this.licensedUser,
      };
      product.license = {
        licenseId: license.licenseID,
        type: license?.licenseType?.toLowerCase(),
        // text: license?.text,
      };

      //* Check for correct price to pass settings to cart
      product.setPrice = product.pricings.find((price) => {
        return price?.pricingType === 'regular' && price?.Active;
      });

      // Set discount and discount type
      product.discount_type = 'subscription_upgrade';
      product.discount = this.upgradeDetails.credit;

      // remove delay_time options
      delete product.delay_time;

      // set subscription ID to add to cart item
      product.subscription_id = subscription;
      // console.log('product2:', product);

      this.$store.dispatch('shop/deleteCart').then(() => {
        this.$store
          .dispatch('shop/createCart')
          .then((res) => {
            product.cartID = res?.cartID;

            this.$store
              .dispatch('shop/addProductToCart', product)
              .then(() => {
                // console.log('addProductToCart res:', res);
                this.$router.push({
                  name: 'Checkout',
                  params: {
                    title: 'checkout',
                  },
                });
              })
              .catch((err) => {
                try {
                  console.error('error UP4002:', err);
                  const {
                    data: { error = {} },
                  } = err;
                  // const errMsg = error.msg.includes('pay and pause')
                  //   ? 'Unable to add item to cart due to existing subscription for same product.'
                  //   : error.msg;

                  if (error?.msg?.includes('pay and pause')) {
                    // this.selectLicenseDialog = false;
                    // this.customDialog = true;
                    return;
                  }

                  // this.selectLicenseDialog = false;
                  this.$root.vtoast.show({
                    text: `ERROR ${error.code}: ${error.msg}`,
                    color: 'error',
                  });
                  // this.customDialog = false;
                } catch (error) {
                  // this.selectLicenseDialog = false;
                  // this.customDialog = false;
                  console.error('error:', error);
                  this.$root.vtoast.show({
                    text: `Error adding item to cart.`,
                    color: 'error',
                  });
                }
              });
          })
          .catch((error) => {
            // this.selectLicenseDialog = false;
            console.error('error UP4001A:', error);
            this.$root.vtoast.show({
              text: `Error ${error?.status} (P4001A): Problem adding product to cart.`,
              color: 'error',
            });
          });
      });
    },

    upgradeProductFilteredList(products = [], currentProduct = {}) {
      // console.log('products:', products);
      // EX1 = Expanded
      // BS1= Basic
      // IRS or IRS1= Improve
      // PLA = Play
      // ENJ = Enjoy

      // 1 Improve
      // 2 Basic
      // 3 Play
      // 4 Enjoy
      // 5 Expanded

      // Used when doing upgrade check here instead of server
      // const currentSkuSuffix = this.getSkuSuffix(currentProduct?.sku);
      // const currentIndex = this.skuHierarchy.indexOf(currentSkuSuffix);
      // if (currentIndex === -1) {
      //   return [];
      // }

      // Define the desired order
      const upgradeOrder = ['improve', 'basic', 'play', 'enjoy', 'expanded'];

      const filteredUpgradeProducts = products
        // ?.filter((product) => {
        //   const productSkuSuffix = this.getSkuSuffix(product.skuCode);
        //   const productIndex = this.skuHierarchy.indexOf(productSkuSuffix);
        //   return productIndex > currentIndex; // Only return higher-ranked SKUs
        // })
        ?.filter((product) => {
          return product.can_upgrade;
        })
        ?.sort((a, b) => {
          // Extract the keyword from `webTitle`
          const keywordA =
            upgradeOrder.find((word) => {
              return a.webTitle?.toLowerCase()?.includes(word);
            }) || '';
          const keywordB =
            upgradeOrder.find((word) => {
              return b.webTitle?.toLowerCase()?.includes(word);
            }) || '';

          // Compare based on the upgradeOrder
          return upgradeOrder.indexOf(keywordA) - upgradeOrder.indexOf(keywordB);
        });
      // console.log('filteredUpgradeProducts:', filteredUpgradeProducts);

      return filteredUpgradeProducts;
    },

    upgradeStepBack() {
      this.upgradeStep = 1;
      this.upgradeDetails = null;
    },

    getSkuSuffix(sku) {
      if (sku.toLowerCase().includes('pc')) {
        return sku?.slice(4); // Remove prefixes
      }

      return sku?.slice(2); // Remove prefixes

      // // option to check first characters
      // const match = sku.match(/^[A-Z]{2,4}(?=[A-Z0-9]{2,4}$)/); // Match consecutive uppercase letters at the start
      // console.log('match:', match);
      // if (match) {
      //   return sku.slice(match[0].length); // Remove the prefix
      // }
      // return sku;
    },

    // Subscription check and dialog display function for subscription cancellation
    cancelSubscriptionCheck(
      origTransactionID,
      title,
      licenseID,
      transactionID,
      productCategory,
      licenseType
    ) {
      // Check to make sure we get the stripe sub_ subscription ID
      const subscriptionID = origTransactionID?.startsWith('sub')
        ? origTransactionID
        : transactionID;

      // If mobile subscription through iOS, display iOS cancellation dialog
      if (
        licenseType === 'mobile' &&
        productCategory === 'licenseSubscription' &&
        !subscriptionID.startsWith('sub_')
      ) {
        this.mobileSubDialog = true;
        return;
      }

      // TG Admin added subscription. Can't cancel by normal means. Display contact TG dialog.
      if (
        origTransactionID.startsWith('trugolf_admin') ||
        (!origTransactionID.startsWith('sub_') && !transactionID.startsWith('sub_'))
      ) {
        this.tgSubCancelDialog = true;
        return;
      }

      // Set subscription info to use for dialog display and pass to cancelSubscription function
      this.subscriptionInfoToCancel = {
        origTransactionID,
        title,
        licenseID,
        transactionID,
        productCategory,
        licenseType,
      };

      this.cancelSubDialog = true;
    },

    // Function call to create payload and emit cancellation trigger
    cancelSubscription({
      origTransactionID,
      title,
      licenseID,
      transactionID,
      productCategory,
      licenseType,
    }) {
      // Trigger loading indicator
      this.subCancelLoading = true;

      // Create subscription cancel payload
      const payload = {
        stripeSubscriptionID: origTransactionID.startsWith('sub')
          ? origTransactionID
          : transactionID,
        title,
        licenseID,
      };

      // Emit cancelled with payload to trigger cancel function
      this.$emit('cancelled', payload);

      // Close dialog and cancel loading
      setTimeout(() => {
        this.closeCancelSubscription();
      }, 1000);
    },

    checkExpiration(date) {
      return date ? moment(new Date(date)).isBefore(moment()) : false;
    },

    checkFarFutureExpiration(date) {
      return date ? moment(new Date(date)).isAfter('2070-01-01', 'year') : false;
    },

    checkForProductCode(id) {
      if (
        id?.includes('trugolf_admin') ||
        id?.includes('sub_') ||
        id?.includes('root_') ||
        id?.includes('user_')
      ) {
        return false;
      }
      const multiSkuTransationID = id?.slice(0, 23);
      return id?.length === 23 || multiSkuTransationID?.length === 23;
    },

    deactivateProduct(
      transactionID = '',
      title = '',
      licenseID = '',
      originalTransactionIDSubID = ''
    ) {
      const subscriptionMsg = `This will immediately cancel the subscription and remove all related permissions.`;
      const answer = confirm(
        `Are you sure you want to deactivate the product: ${title}? ${
          originalTransactionIDSubID ? subscriptionMsg : ''
        }`
      );
      if (answer) {
        const payload = {
          transactionID,
          licenseID,
          subscriptionID: originalTransactionIDSubID,
        };
        this.$emit('deactivated', payload);
      }
    },

    isCancelled(status) {
      return status?.toLowerCase()?.includes('cancel') || false;
    },

    hasStripeSub(originalTransactionID = '', transactionID = '') {
      const isSub = originalTransactionID?.includes('sub_') || transactionID?.includes('sub_');
      return isSub;
    },

    // Function to check if 'Cancel Renewal' button should be disabled or active
    disableCancelCheck(originalTransactionID, transactionID) {
      const hasStripe = this.hasStripeSub(originalTransactionID, transactionID);
      if (hasStripe) {
        return false;
      }
      return true;
    },

    // Subscription dialog close and reset data
    closeCancelSubscription() {
      this.cancelSubDialog = false;
      this.tgSubCancelDialog = false;
      this.subscriptionInfoToCancel = null;
      this.subCancelLoading = false;
    },

    getSubID(origTransactionID, transactionID) {
      // Check to make sure we get the stripe sub_ subscription ID
      const subscriptionID = origTransactionID?.startsWith('sub')
        ? origTransactionID
        : transactionID;
      return subscriptionID || origTransactionID;
    },

    subStatusFormat(status) {
      switch (status?.toLowerCase()) {
        case 'trialing':
          return 'Active';
        default:
          return status;
      }
    },
  },
};
</script>

<style scoped>
.container.product-license {
  min-height: calc(10vh - 65px) !important;
  width: 100% !important;
}

.licenses {
  box-shadow: none;
}

.tab-card {
  background-color: rgba(0, 0, 0, 0) !important;
  min-height: 150px;
}

ul {
  list-style: none;
  padding: 0;
  columns: 3;
  -webkit-columns: 3;
  -moz-columns: 3;
}

.theme--dark >>> .info--text {
  color: #ba2127 !important;
  color: var(--primary) !important;
  caret-color: #ba2127 !important;
  caret-color: var(--primary) !important;
}
</style>
